import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, 
  Check, 
  Zap,
  Sparkles,
  Shield,
  Users,
  Mail,
  BarChart3,
  Palette,
  FileSpreadsheet,
  ArrowRight,
  HelpCircle,
  CreditCard
} from 'lucide-react';

const SUPPORT_EMAIL = 'hello@intakepal.com';

const PLAN_FEATURES = [
  { icon: Zap, text: 'Unlimited lead capture', description: 'Never miss a potential client' },
  { icon: Sparkles, text: 'Automated case summaries', description: 'Instant lead prioritization' },
  { icon: Mail, text: 'Automated email/SMS follow-ups', description: 'Nurture leads automatically' },
  { icon: Users, text: 'Team member accounts', description: 'Collaborate with your staff' },
  { icon: Palette, text: 'Custom branding & widget', description: 'Match your firm\'s identity' },
  { icon: BarChart3, text: 'Analytics dashboard', description: 'Track performance metrics' },
  { icon: Shield, text: 'Lead scoring & prioritization', description: 'Focus on hot leads first' },
  { icon: FileSpreadsheet, text: 'CMS integrations', description: 'Clio, Filevine, and 20+ more' },
];

const FAQ_ITEMS = [
  {
    q: 'What happens after my 7-day trial?',
    a: 'After your trial ends, your card will be charged $179.99/month. You\'ll receive a reminder email before your trial ends. If you cancel during the trial, you won\'t be charged.'
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes! You can cancel your subscription at any time from your dashboard. There are no long-term contracts or cancellation fees.'
  },
  {
    q: 'Do you offer annual billing discounts?',
    a: 'Contact us at hello@intakepal.com to discuss annual billing options and potential discounts for your firm.'
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) as well as Google Pay and Apple Pay.'
  },
  {
    q: 'Is my data secure?',
    a: 'Absolutely. We use industry-standard encryption, and all data is stored securely. We follow legal industry data protection best practices.'
  },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/chat-demo">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium">
                  Demo
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="pricing-get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Everything you need to automate client intake. Start with a 7-day free trial.
          </p>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <Card className="border-2 border-teal-500 shadow-2xl overflow-hidden" data-testid="pricing-card">
            <CardHeader className="text-center pb-2 bg-gradient-to-br from-teal-500 to-emerald-500 text-white py-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4 mx-auto">
                <Sparkles className="w-4 h-4" />
                All Features Included
              </div>
              <CardTitle className="text-3xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal Pro
              </CardTitle>
              <CardDescription className="text-teal-100 text-base">
                Complete client intake automation
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8 pb-8">
              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-6xl font-bold text-slate-900">$179.99</span>
                  <span className="text-xl text-slate-500">/month</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold">
                  <Zap className="w-4 h-4" />
                  7-day free trial included
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {PLAN_FEATURES.map((feature) => (
                  <div key={feature.text} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <feature.icon className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <span className="text-slate-900 font-medium">{feature.text}</span>
                      <p className="text-sm text-slate-500">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link to="/register">
                <Button
                  className="w-full bg-teal-500 hover:bg-teal-600 text-lg py-6 h-auto"
                  data-testid="pricing-start-trial-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Start Your Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>

              <p className="text-center text-sm text-slate-500 mt-4">
                Cancel anytime during your trial. You won&apos;t be charged until after 7 days.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap justify-center gap-8 text-slate-500">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-teal-500" />
              <span>No setup fees</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-teal-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-teal-500" />
              <span>24/7 support</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-teal-500" />
              <span>Secure payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-3xl font-bold text-slate-900 text-center mb-12"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {FAQ_ITEMS.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                  {item.q}
                </h3>
                <p className="text-slate-600 ml-8">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-2xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Have Questions?
          </h2>
          <p className="text-slate-600 mb-6">
            Our team is here to help you get started with IntakePal.
          </p>
          <a 
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
          >
            <Mail className="w-5 h-5" />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-teal-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-teal-600">
                Terms of Service
              </Link>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} IntakePal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
